import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  MessageCircle,
  Sparkles,
  Star,
  Zap,
} from "lucide-react";

import Marquee from "../components/Marquee";
import useSEO from "../lib/useSEO";
import { buildWhatsAppLink } from "../lib/constants";

const PROBLEMS = [
  "Tim kamu butuh seragam tapi vendor lama selalu telat?",
  "Mau bikin merch brand sendiri tapi minimum order kebanyakan?",
  "Print apparel sebelumnya luntur dan retak setelah beberapa kali cuci?",
  "Bingung bikin desain dari nol untuk komunitas kamu?",
];

const SOLUTIONS = [
  {
    icon: Zap,
    title: "Produksi 5–7 Hari",
    desc: "Timeline tegas yang kami komit setelah desain di-ACC.",
  },
  {
    icon: Sparkles,
    title: "Tanpa Minimum Order",
    desc: "Mau pesan 1 pcs? Bisa. Mau 50 pcs? Bisa juga, harga sama-sama bersaing.",
  },
  {
    icon: CheckCircle2,
    title: "Sublimasi Anti Luntur",
    desc: "Tinta menyerap ke serat kain, hasil tahan tahunan walau dicuci ratusan kali.",
  },
  {
    icon: Star,
    title: "Tim Desain Standby",
    desc: "Brief saja, sisanya kami eksekusi. Gratis 2x revisi sampai cocok.",
  },
];

const SAMPLES = [
  "https://images.unsplash.com/photo-1768084356884-22bb77e76931?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxzdHJlZXR3ZWFyJTIwbW9kZWwlMjBkYXJrfGVufDB8fHx8MTc3NzYxNzI4MHww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1760174012435-630a17a434ed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxmdXRzYWwlMjBwbGF5ZXIlMjBhY3Rpb258ZW58MHx8fHwxNzc3NjE3MjgwfDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1564115547164-8acb1a158428?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxqYXBhbmVzZSUyMHN0cmVldHdlYXIlMjBkYXJrfGVufDB8fHx8MTc3NzYxNzI5Nnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1767036841778-294c81791933?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxyYWNpbmclMjBqYWNrZXQlMjBkYXJrfGVufDB8fHx8MTc3NzYxNzI5Nnww&ixlib=rb-4.1.0&q=85",
];

const TESTI_QUOTES = [
  {
    q: "Pesan 22 jersey untuk tim sekolah, semua tepat waktu. Anak-anak senang banget pas coba pertama kali.",
    n: "Dimas A.",
  },
  {
    q: "Sebagai brand owner yang produksi rutin, kerja sama dengan Ace bikin tenang. Kualitas dan deadline jaga konsisten.",
    n: "Farah A.",
  },
];

export default function CustomOrderPage() {
  useSEO({
    title:
      "Custom Order Apparel — Bebas Desain, Tanpa Minimum | Ace Sublimasi Nusantara",
    description:
      "Cara order custom apparel di Ace Sublimasi Nusantara. Brief, mock-up 24 jam, produksi 5–7 hari. Cocok untuk tim, komunitas, brand, dan personal.",
    keywords:
      "custom apparel indonesia, jasa custom jersey, custom jaket anime, custom haori, racing custom, no minimum order",
    canonical: window.location.origin + "/custom",
  });

  return (
    <div data-testid="custom-page" className="border-b border-[#1f1f1f]">
      {/* HERO */}
      <section className="relative min-h-[80vh] flex items-center border-b border-[#1f1f1f] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1771873679764-4e5503b69040?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbmVvbiUyMHRleHR1cmV8ZW58MHx8fHwxNzc3NjE3Mjk2fDA&ixlib=rb-4.1.0&q=85"
            alt="texture"
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-[#050505]/80" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#CCFF00] mb-4">
            / Custom Order Apparel
          </p>
          <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl uppercase leading-[0.9] text-white max-w-5xl">
            Bawa Idemu, <br />
            <span className="text-[#CCFF00]">Pulang Pakai Apparel.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg text-[#C2C2C2]">
            Mulai dari brief sederhana sampai apparel siap dipakai — semua dikerjakan langsung
            oleh tim Ace di Wonosobo. Jersey futsal, jaket anime, haori, racing apparel, atau streetwear.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <a
              href={buildWhatsAppLink(
                "Halo Admin Ace, saya mau custom apparel. Boleh konsultasi dulu?"
              )}
              target="_blank"
              rel="noreferrer"
              data-testid="custom-hero-wa"
              className="btn-primary"
            >
              <MessageCircle className="w-5 h-5" />
              Mulai Custom Sekarang
            </a>
            <Link to="/shop" className="btn-ghost" data-testid="custom-hero-shop">
              Lihat Katalog Dulu
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Marquee
        items={["BRIEF", "MOCK-UP", "PRODUKSI", "DELIVER", "PAKAI BANGGA"]}
      />

      {/* PROBLEM */}
      <section className="py-20 sm:py-24 border-b border-[#1f1f1f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#FF3B30] mb-4">
              / Pernah Ngalamin Ini?
            </p>
            <h2 className="font-display text-4xl sm:text-5xl uppercase leading-none text-white">
              Bikin Apparel <br />
              <span className="text-outline">Harusnya Nggak Ribet.</span>
            </h2>
          </div>
          <div className="md:col-span-7">
            <ul className="border border-[#262626]">
              {PROBLEMS.map((p) => (
                <li
                  key={p}
                  className="px-5 md:px-7 py-5 border-b border-[#262626] last:border-b-0 flex items-start gap-3 text-base md:text-lg text-white"
                >
                  <span className="font-display text-2xl text-[#FF3B30] leading-none shrink-0">
                    ×
                  </span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SOLUTION */}
      <section className="py-20 sm:py-24 border-b border-[#1f1f1f] bg-[#070707]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#CCFF00] mb-4">
              / Solusi Kami
            </p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-none text-white">
              Cara Kerja Kami <br />
              <span className="text-[#CCFF00]">Beda. Lebih Simpel.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-[#262626] divide-y md:divide-y-0 md:divide-x divide-[#262626]">
            {SOLUTIONS.map((s, idx) => (
              <div
                key={s.title}
                data-testid={`solution-${idx}`}
                className="p-6 md:p-8 group hover:bg-[#0e0e0e] transition-colors"
              >
                <div className="w-12 h-12 border border-[#262626] inline-flex items-center justify-center text-[#CCFF00] group-hover:bg-[#CCFF00] group-hover:text-black transition-all">
                  <s.icon className="w-5 h-5" />
                </div>
                <h3 className="font-display text-xl uppercase mt-5 text-white tracking-tight">
                  {s.title}
                </h3>
                <p className="mt-3 text-sm text-[#A3A3A3]">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SAMPLES */}
      <section className="py-20 sm:py-24 border-b border-[#1f1f1f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-12 gap-6 mb-10">
            <div className="md:col-span-7">
              <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#CCFF00] mb-4">
                / Contoh Hasil
              </p>
              <h2 className="font-display text-4xl sm:text-5xl uppercase leading-none text-white">
                Real Project, Real Customer.
              </h2>
            </div>
            <div className="md:col-span-4 md:col-start-9 flex items-end">
              <p className="text-base text-[#A3A3A3]">
                Beberapa hasil eksekusi tim kami untuk customer di seluruh Indonesia.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {SAMPLES.map((src, i) => (
              <div
                key={i}
                data-testid={`sample-${i}`}
                className="aspect-[4/5] border border-[#262626] overflow-hidden group"
              >
                <img
                  src={src}
                  alt={`Sample ${i + 1}`}
                  className="w-full h-full object-cover grayscale-[40%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="py-20 sm:py-24 border-b border-[#1f1f1f] bg-[#070707]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6">
          {TESTI_QUOTES.map((t) => (
            <div
              key={t.n}
              className="border border-[#262626] p-7 bg-[#0a0a0a] flex flex-col"
            >
              <div className="flex gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className="w-4 h-4 fill-[#CCFF00] text-[#CCFF00]"
                  />
                ))}
              </div>
              <p className="mt-4 text-lg text-white leading-relaxed">“{t.q}”</p>
              <p className="mt-6 pt-5 border-t border-[#262626] font-display text-lg uppercase text-white">
                {t.n}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 sm:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#CCFF00] mb-4">
            / Saatnya Eksekusi
          </p>
          <h2 className="font-display text-5xl sm:text-7xl uppercase leading-[0.9] text-white">
            Cukup Chat. <br />
            <span className="text-[#CCFF00]">Selesai.</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-[#C2C2C2]">
            Kirim referensi atau ide kasar kamu ke admin via WhatsApp. Tim kami akan bantu
            eksekusi sampai apparel custom kamu sampai ke tangan.
          </p>
          <a
            href={buildWhatsAppLink(
              "Halo Admin Ace, saya mau custom apparel. Boleh kirim brief sekarang?"
            )}
            target="_blank"
            rel="noreferrer"
            data-testid="custom-final-wa"
            className="btn-primary mt-8 inline-flex"
          >
            <MessageCircle className="w-5 h-5" />
            Chat Admin & Mulai
          </a>
          <p className="mt-6 text-xs uppercase tracking-widest text-[#A3A3A3]">
            Online setiap hari · Balas cepat dalam menit
          </p>
        </div>
      </section>
    </div>
  );
}
